// src/services/aiResponseHelpers.js
//
// Helpers puros (sin llamadas de red) para controlar la longitud de las
// respuestas de la IA y garantizar la integridad de los enlaces/datos.
// Probados en aiResponseHelpers.test.js — mantener la cobertura al editar.

// Caracteres de puntuación que suelen pegarse al final de una URL en texto.
const TRAILING_PUNCTUATION = /[.,;:!?)\]}"'»>]+$/;

/**
 * Extrae todas las URLs (http/https) de un texto, limpiando la puntuación final.
 * @param {string} text
 * @returns {string[]}
 */
export const extractLinks = (text) => {
  if (!text || typeof text !== 'string') return [];
  const matches = text.match(/https?:\/\/[^\s<>"']+/gi) || [];
  return matches.map((url) => url.replace(TRAILING_PUNCTUATION, ''));
};

/**
 * Devuelve el host de una URL, o '' si no es parseable.
 */
const hostOf = (url) => {
  try {
    return new URL(url).host.toLowerCase();
  } catch (e) {
    const m = url.match(/^https?:\/\/([^/\s]+)/i);
    return m ? m[1].toLowerCase() : '';
  }
};

// Presets de longitud: directriz para el prompt + presupuesto de tokens.
const LENGTH_PRESETS = {
  breve: {
    directive:
      'LONGITUD: BREVE. Responde en MÁXIMO 5 líneas. Ve directo al grano, sin rodeos ni relleno. ' +
      'Una idea por frase. NADA de encabezados, listas largas ni secciones. ' +
      'Incluye solo lo esencial que responde la pregunta y el enlace o dato pedido.',
    maxOutputTokens: 800,
  },
  normal: {
    directive:
      'LONGITUD: NORMAL. Responde de forma clara y completa pero concisa. ' +
      'Evita relleno y repeticiones. Usa listas cortas solo si ayudan a la claridad.',
    maxOutputTokens: 2048,
  },
  detallado: {
    directive:
      'LONGITUD: DETALLADA. Puedes extenderte lo necesario, con encabezados, listas y ' +
      'secciones cuando el tema lo amerite.',
    maxOutputTokens: 8192,
  },
};

/**
 * Devuelve la directriz de longitud y el presupuesto de tokens para un modo.
 * Modos válidos: 'breve' | 'normal' | 'detallado'. Cualquier otro → 'normal'.
 * @param {string} mode
 * @returns {{directive: string, maxOutputTokens: number}}
 */
export const buildLengthDirective = (mode) => {
  return LENGTH_PRESETS[mode] || LENGTH_PRESETS.normal;
};

/**
 * Construye un bloque "DATOS EXACTOS" listando cada snippet relevante con sus
 * enlaces, para que la IA los copie literalmente en lugar de inventarlos.
 * @param {Array<{title?: string, content?: string}>} snippets
 * @returns {string}
 */
export const extractExactDataBlock = (snippets = []) => {
  const lines = [];
  for (const snippet of snippets) {
    const links = extractLinks(snippet?.content || '');
    if (links.length > 0) {
      const title = (snippet?.title || 'Sin título').trim();
      lines.push(`- "${title}": ${links.join(', ')}`);
    }
  }
  return lines.join('\n');
};

// Marcadores con los que la IA delimita el mensaje listo para enviar (modo Gestor).
const MESSAGE_BLOCK_RE = /<<<\s*MENSAJE\s*>>>\s*([\s\S]*?)\s*<<<\s*FIN\s*>>>/i;

/**
 * Separa la introducción conversacional del mensaje listo para enviar, que la
 * IA delimita con <<<MENSAJE>>> ... <<<FIN>>>. Permite renderizar el mensaje en
 * su propia tarjeta (estilo ChatGPT) y copiar/editar solo esa parte.
 * @param {string} text
 * @returns {{intro: string, message: string|null}} message es null si no hay bloque.
 */
export const extractMessageBlock = (text) => {
  if (!text || typeof text !== 'string') return { intro: '', message: null };
  const m = text.match(MESSAGE_BLOCK_RE);
  if (!m) return { intro: '', message: null };
  const message = (m[1] || '').trim();
  if (!message) return { intro: '', message: null };
  const intro = text.slice(0, m.index).trim();
  return { intro, message };
};

// Frases de relleno con las que la IA suele introducir una respuesta.
const PREAMBLE_RE = /^¡?\s*(claro|por\s?supuesto|con gusto|perfecto|entendido|aqu[ií]\s+(tienes|est[áa])|esta es (la|tu) respuesta|te (ayudo|comparto)|por su puesto)\b/i;

/**
 * Quita el preámbulo conversacional ("Claro, aquí tienes:", "Esta es la
 * respuesta:", etc.) para que al copiar quede solo el mensaje listo para enviar.
 * Conservador: si quitarlo dejaría el texto vacío, devuelve el original.
 * @param {string} text
 * @returns {string}
 */
export const stripPreamble = (text) => {
  if (!text || typeof text !== 'string') return text;

  const s = text.replace(/^\s+/, '');
  const newlineIdx = s.indexOf('\n');
  const firstLine = newlineIdx === -1 ? s : s.slice(0, newlineIdx);
  const rest = newlineIdx === -1 ? '' : s.slice(newlineIdx + 1);

  if (!PREAMBLE_RE.test(firstLine)) return text;

  // Si el preámbulo lleva ":" y hay contenido después en la misma línea, conservarlo.
  const colonIdx = firstLine.indexOf(':');
  if (colonIdx !== -1 && firstLine.slice(colonIdx + 1).trim()) {
    const after = firstLine.slice(colonIdx + 1).trim();
    const result = (after + (rest ? '\n' + rest : '')).trim();
    return result || text;
  }

  // Si no, descartar la línea de preámbulo completa.
  const result = rest.replace(/^\s+/, '');
  return result.trim() ? result : text;
};

/**
 * Revisa la respuesta generada por la IA y corrige enlaces inventados.
 *
 * Para cada enlace en la salida que NO exista literal en la base de conocimiento:
 *  - Si la base tiene algún enlace del mismo host, lo reemplaza por el del
 *    snippet MÁS relevante (los snippets deben venir ordenados por relevancia).
 *  - Si no hay ningún enlace del mismo host, lo marca como "suspicious".
 *
 * @param {string} output - Texto generado por la IA.
 * @param {Array<{title?: string, content?: string}>} snippets - Snippets relevantes, ordenados.
 * @returns {{text: string, replacements: Array<{from: string, to: string}>, suspicious: string[]}}
 */
export const sanitizeOutputLinks = (output, snippets = []) => {
  const result = { text: output || '', replacements: [], suspicious: [] };
  if (!output) return result;

  // Enlaces permitidos (literales en la base), en orden de relevancia.
  const allowed = [];
  for (const snippet of snippets) {
    for (const link of extractLinks(snippet?.content || '')) {
      if (!allowed.includes(link)) allowed.push(link);
    }
  }
  const allowedSet = new Set(allowed);

  // Enlaces únicos presentes en la salida.
  const outputLinks = [...new Set(extractLinks(output))];

  for (const link of outputLinks) {
    if (allowedSet.has(link)) continue; // enlace correcto, no tocar

    const host = hostOf(link);
    const replacement = allowed.find((a) => hostOf(a) === host);

    if (replacement) {
      result.text = result.text.split(link).join(replacement);
      result.replacements.push({ from: link, to: replacement });
    } else {
      result.suspicious.push(link);
    }
  }

  return result;
};
